'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { MdMeetingRoom } from 'react-icons/md'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useAuth } from '@/store/hooks'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    AppointmentKind,
    getPresenceByAppointment,
    presenceBadgeProps,
    PresenceEntry,
} from '@/lib/api/presence/presence-api'
import PresenceEventList from './presence-event-list'

interface PresenceTimelineProps {
    kind: AppointmentKind
    appointmentId: number
}

/** Quanto tempo o paciente ficou (ou está) no balcão nesta passagem. */
function elapsed(entry: PresenceEntry): string {
    const start = new Date(entry.arrivedAt).getTime()
    const end = entry.leftAt ? new Date(entry.leftAt).getTime() : Date.now()
    const minutes = Math.max(0, Math.floor((end - start) / 60000))
    if (minutes < 60) return `${minutes} min`
    return `${Math.floor(minutes / 60)}h ${minutes % 60}min`
}

/**
 * Histórico de presença de um agendamento, para a tela de detalhe dele.
 *
 * Mostra cada passagem pelo balcão (um agendamento remarcado pode ter mais de
 * uma) e, dentro dela, os movimentos em ordem: chegada, trocas de controle,
 * encaminhamentos de sala e saída. Some por completo quando o paciente nunca
 * passou pelo controle de presença — o card não fica ocupando espaço vazio numa
 * tela que já é longa.
 */
export default function PresenceTimeline({
    kind,
    appointmentId,
}: PresenceTimelineProps) {
    const { canRead } = usePermission()
    const allowed = canRead('bookings', 'presence')
    const { socket, isConnected } = useContext(SocketContext)
    const { user } = useAuth()

    const [entries, setEntries] = useState<PresenceEntry[]>([])
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        const response = await getPresenceByAppointment(kind, appointmentId)
        if (response.success && response.result) {
            setEntries(response.result)
        }
        setLoading(false)
    }, [kind, appointmentId])

    useEffect(() => {
        if (!allowed) return
        load()
    }, [allowed, load])

    // A tela do agendamento costuma ficar aberta na sala do profissional
    // enquanto a recepção mexe no painel: o histórico acompanha sem F5.
    useEffect(() => {
        if (!allowed || !socket || !isConnected || !user?.companyId) return
        const event = `presence-updated-${user.companyId}`
        socket.on(event, load)
        return () => {
            socket.off(event, load)
        }
    }, [allowed, socket, isConnected, user?.companyId, load])

    if (!allowed || loading || entries.length === 0) return null

    return (
        <div className="mb-6">
            <h3 className="mb-3 text-lg font-bold text-base-content">
                Controle de presença
            </h3>

            <div className="flex flex-col gap-4">
                {entries.map(entry => {
                    const badge = presenceBadgeProps(entry.status.color)
                    return (
                        <div
                            key={entry.id}
                            className="rounded-xl bg-base-200/50 p-4"
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <span
                                    className={`badge badge-sm ${badge.className}`}
                                    style={badge.style}
                                >
                                    {entry.status.name}
                                </span>
                                {entry.room && (
                                    <span className="flex items-center gap-1 text-sm text-base-content/70">
                                        <MdMeetingRoom className="text-base" />
                                        {entry.room.name}
                                    </span>
                                )}
                                <span className="text-sm text-base-content/60">
                                    {entry.leftAt
                                        ? `Permaneceu ${elapsed(entry)}`
                                        : `No balcão há ${elapsed(entry)}`}
                                </span>
                            </div>

                            <PresenceEventList events={entry.events ?? []} />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
